import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, Users, X } from "lucide-react";
import { AdminStatusBadge } from "@/components/admin-ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getActorPhotoUrl } from "@/services/firebase/actor";
import type { ArtistAdminRow } from "@/lib/artistAdmin";
import {
  addCastMember,
  castPositionLabel,
  castStatusChips,
  hiddenCastWarning,
  hiddenFromAppCast,
  moveCastMember,
  removeCastMember,
} from "@/lib/cast";

export interface ArtistCastOption {
  id: string;
  label: string;
  /** actors.photoURL (storage path or URL) */
  photoURL?: string;
}

interface ArtistCastPickerProps {
  options: ArtistCastOption[];
  /** actor ids in cast order (index 0 = 主演) */
  value: string[];
  onChange: (value: string[]) => void;
  /** artistAdminList rows by artistId; omit (hub off / not loaded) to hide status chips and the warning. */
  statusById?: ReadonlyMap<string, ArtistAdminRow>;
  label?: string;
}

function Avatar({ photoURL, name }: { photoURL?: string; name: string }) {
  const url = photoURL ? getActorPhotoUrl(photoURL) : "";
  if (url) {
    return (
      <img
        src={url}
        alt={name}
        width={32}
        height={32}
        loading="lazy"
        decoding="async"
        className="h-8 w-8 shrink-0 rounded-full bg-muted object-cover"
      />
    );
  }
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
      <Users className="h-4 w-4 text-muted-foreground" aria-hidden />
    </div>
  );
}

function StatusChips({ row }: { row: ArtistAdminRow | undefined }) {
  const chips = castStatusChips(row);
  if (chips.length === 0) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {chips.map((c) => (
        <AdminStatusBadge key={c.label} label={c.label} tone={c.tone} />
      ))}
    </span>
  );
}

/**
 * 短劇卡司挑選器：搜尋加入、上下移動排序、移除。value 的順序就是 dramas.actors 的順序（第一位＝主演）。
 */
export default function ArtistCastPicker({ options, value, onChange, statusById, label = "演員（卡司）" }: ArtistCastPickerProps) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const optionById = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);
  const candidates = useMemo(() => {
    const term = search.trim().toLowerCase();
    return options.filter((o) => !value.includes(o.id) && (!term || o.label.toLowerCase().includes(term)));
  }, [options, value, search]);

  const nameOf = (id: string) => optionById.get(id)?.label ?? id;
  const hiddenNames = statusById ? hiddenFromAppCast(value, statusById).map(nameOf) : [];
  const warning = hiddenCastWarning(hiddenNames);

  const add = (id: string) => {
    onChange(addCastMember(value, id));
    setSearch("");
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="artist-cast-search">{label}</Label>
      <p className="text-xs text-muted-foreground">順序即 App 卡司順序，第 1 位為主演。</p>

      <div ref={boxRef} className="relative">
        <Input
          id="artist-cast-search"
          value={search}
          placeholder="搜尋並加入演員..."
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls="artist-cast-options"
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (candidates[0]) add(candidates[0].id);
            } else if (e.key === "Escape") {
              setOpen(false);
            }
          }}
        />
        {open && (
          <div
            id="artist-cast-options"
            role="listbox"
            className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md"
          >
            {candidates.length > 0 ? (
              candidates.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => add(o.id)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:outline-none"
                >
                  <span className="min-w-0 flex-1 truncate">{o.label}</span>
                  <StatusChips row={statusById?.get(o.id)} />
                </button>
              ))
            ) : (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">沒有可加入的演員</div>
            )}
          </div>
        )}
      </div>

      {value.length > 0 ? (
        <ol className="divide-y rounded-md border" aria-label="已選卡司">
          {value.map((id, index) => {
            const name = nameOf(id);
            const known = optionById.has(id);
            return (
              <li key={id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                <span className="w-14 shrink-0 text-xs font-medium text-muted-foreground">
                  {index + 1}. {index === 0 ? "主演" : ""}
                </span>
                <Avatar photoURL={optionById.get(id)?.photoURL} name={name} />
                <span className={`min-w-0 flex-1 truncate text-sm ${known ? "" : "font-mono text-muted-foreground"}`}>
                  {name}
                </span>
                <StatusChips row={statusById?.get(id)} />
                <span className="flex shrink-0 items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={index === 0}
                    aria-label={`將 ${name} 上移（目前${castPositionLabel(index)}）`}
                    onClick={() => onChange(moveCastMember(value, index, -1))}
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={index === value.length - 1}
                    aria-label={`將 ${name} 下移（目前${castPositionLabel(index)}）`}
                    onClick={() => onChange(moveCastMember(value, index, 1))}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    aria-label={`從卡司移除 ${name}`}
                    onClick={() => onChange(removeCastMember(value, id))}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </span>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="rounded-md border border-dashed px-3 py-4 text-center text-sm text-muted-foreground">尚未加入演員</p>
      )}

      {warning && (
        <p role="status" className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
          <span>{warning}</span>
        </p>
      )}
    </div>
  );
}
